/**
 * Meet Vyper — video testimonial carousel.
 *
 * The Replo original fakes an infinite loop by cloning its six slides into nineteen. Cloning in
 * the markup would mean a merchant editing the same testimonial three times, so the slides here
 * are the real six and the clones are made at runtime: one set either side, with the transition
 * suppressed when we jump back to the middle. From the outside it just scrolls forever.
 *
 * The centre slide is the active one — it grows to full size and is the one that plays.
 */
(function () {
  const initialized = new WeakSet();

  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const track = root.querySelector('[data-track]');
    const viewport = root.querySelector('[data-viewport]');
    const dotsWrap = root.querySelector('[data-dots]');
    if (!track || !viewport) return;

    const real = Array.from(track.children);
    const count = real.length;
    if (!count) return;

    // Dots are built here rather than in Liquid: the slides live inside a group block, and a
    // section cannot count a group block's children.
    if (dotsWrap && !dotsWrap.children.length) {
      real.forEach((_, i) => {
        const d = document.createElement('button');
        d.type = 'button';
        d.className = 'vp-mv-testimonials__dot';
        d.setAttribute('aria-label', `Go to testimonial ${i + 1}`);
        d.setAttribute('aria-current', String(i === 0));
        dotsWrap.appendChild(d);
      });
    }

    const loop = root.dataset.loop !== 'false' && count > 2;

    // One clone set either side is enough: the jump happens the moment an edge is reached.
    if (loop) {
      real.forEach((s) => track.appendChild(cloneSlide(s)));
      for (let i = real.length - 1; i >= 0; i--) track.insertBefore(cloneSlide(real[i]), track.firstChild);
    }

    const slides = Array.from(track.children);
    const offset = loop ? count : 0;
    let index = 0;

    function cloneSlide(s) {
      const c = s.cloneNode(true);
      c.setAttribute('data-clone', 'true');
      c.setAttribute('aria-hidden', 'true');
      c.querySelectorAll('button, a').forEach((el) => el.setAttribute('tabindex', '-1'));
      return c;
    }

    /** Centre the slide at `pos` in the viewport. */
    function offsetFor(pos) {
      const slide = slides[pos];
      if (!slide) return 0;
      return slide.offsetLeft + slide.offsetWidth / 2 - viewport.clientWidth / 2;
    }

    function paint(animate) {
      const pos = index + offset;
      if (!animate) track.style.transition = 'none';
      track.style.transform = `translate3d(${-offsetFor(pos)}px, 0, 0)`;
      if (!animate) {
        // force a reflow so the suppressed transition does not apply to the next change
        void track.offsetWidth;
        track.style.transition = '';
      }
      slides.forEach((s, i) => s.setAttribute('data-active', String(i === pos)));
      if (dotsWrap) {
        Array.from(dotsWrap.children).forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      }
      stopAll();
    }

    function stopAll() {
      slides.forEach((s) => {
        const v = s.querySelector('video');
        if (v && !v.paused) v.pause();
        s.setAttribute('data-playing', 'false');
      });
    }

    function go(delta) {
      index += delta;
      if (!loop) {
        index = Math.max(0, Math.min(count - 1, index));
        paint(true);
        return;
      }
      paint(true);
      if (index < 0 || index >= count) {
        // let the animation land, then silently re-centre on the equivalent real slide
        const settle = () => {
          index = ((index % count) + count) % count;
          paint(false);
          track.removeEventListener('transitionend', settle);
        };
        track.addEventListener('transitionend', settle);
      }
    }

    root.querySelectorAll('[data-prev]').forEach((b) => b.addEventListener('click', () => go(-1)));
    root.querySelectorAll('[data-next]').forEach((b) => b.addEventListener('click', () => go(1)));

    if (dotsWrap) {
      Array.from(dotsWrap.children).forEach((d, i) =>
        d.addEventListener('click', () => { index = i; paint(true); })
      );
    }

    // Clicking a slide's poster plays that clip; clicking an off-centre one brings it in first.
    track.addEventListener('click', (e) => {
      const slide = e.target.closest('[data-slide]');
      if (!slide) return;
      const pos = slides.indexOf(slide);
      if (pos < 0) return;
      if (slide.getAttribute('data-active') !== 'true') {
        index = ((pos - offset) % count + count) % count;
        paint(true);
        return;
      }
      const video = slide.querySelector('video');
      if (!video) return;
      if (video.paused) {
        stopAll();
        slide.setAttribute('data-playing', 'true');
        video.play().catch(() => slide.setAttribute('data-playing', 'false'));
      } else {
        video.pause();
        slide.setAttribute('data-playing', 'false');
      }
    });

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { go(-1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { go(1); e.preventDefault(); }
    });

    // Slide widths come from custom properties, so a breakpoint change moves the centre point.
    let raf;
    window.addEventListener('resize', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => paint(false));
    });

    paint(false);
  }

  const initAll = () => document.querySelectorAll('[data-vp-mv-testimonials]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
